import React, { useState, useEffect } from 'react';
import { X, Plus, Home, Edit, Trash2, MapPin, Bed, Bath, DollarSign, Image as ImageIcon, ArrowLeft } from 'lucide-react';
import api from '../services/api';
import toast from 'react-hot-toast';
import modalsBg from '../assets/Modals.jpg';
import SkeletonListing from './SkeletonListing';

const MAX_IMAGE_COUNT = 10;
const MAX_IMAGE_SIZE_BYTES = 10 * 1024 * 1024;

// Downscale + re-encode images in the browser before upload so phone photos
// (which are often 5-20MB, sometimes HEIC) upload reliably on mobile networks.
const compressImage = (file, maxDimension = 1600, quality = 0.8) =>
  new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      try {
        let { width, height } = img;
        const scale = Math.min(1, maxDimension / Math.max(width, height));
        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(width * scale));
        canvas.height = Math.max(1, Math.round(height * scale));
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        canvas.toBlob(
          (blob) => {
            URL.revokeObjectURL(objectUrl);
            if (!blob) {
              reject(new Error('Could not compress image'));
              return;
            }
            const safeName = file.name.replace(/[^.\w-]/g, '_');
            resolve(new File([blob], safeName, { type: 'image/jpeg', lastModified: Date.now() }));
          },
          'image/jpeg',
          quality
        );
      } catch (error) {
        URL.revokeObjectURL(objectUrl);
        reject(error);
      }
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Could not read image'));
    };
    img.src = objectUrl;
  });

const ListingsModal = ({ isOpen, onClose, user, onListingChange }) => {
  const [view, setView] = useState('list'); // 'list' or 'form'
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [processingImages, setProcessingImages] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [editingListing, setEditingListing] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    property_type: 'Apartment',
    price: '',
    price_type: 'Fixed Price',
    location: '',
    city: 'Accra',
    bedrooms: 1,
    bathrooms: 1,
    square_feet: '',
    amenities: [],
    images: []
  });
  const [imagePreview, setImagePreview] = useState([]);
  const [pendingImageFiles, setPendingImageFiles] = useState([]);

  const propertyTypes = ['Apartment', 'House', 'Studio', 'Bedsitter', 'Mini Flat', 'Bungalow', 'Mansion'];
  const amenitiesList = ['WiFi', 'Parking', 'Security', 'Generator', 'Water', 'AC', 'Furnished', 'Pool', 'Gym', 'Garden', 'Electricity', 'Balcony', 'Dining Area', 'Tiled floor', 'Pop ceiling', 'Kitchen cabinets', 'Kitchen shelf', 'Prepaid meter', 'Microwave', 'Refrigerator', 'TV', 'In-unit washer/dryer', 'Doorman', 'Pet friendly', 'Garage', 'Backyard', 'Dishwasher'];

  useEffect(() => {
    if (isOpen && user) {
      fetchUserListings();
    }
  }, [isOpen, user]);

  useEffect(() => {
    if (isOpen) {
      return undefined;
    }

    pendingImageFiles.forEach((item) => {
      if (item.preview) {
        URL.revokeObjectURL(item.preview);
      }
    });

    return undefined;
  }, [isOpen, pendingImageFiles]);

  const fetchUserListings = async () => {
    try {
      setLoading(true);
      const data = await api.getUserListings();
      setListings(data);
    } catch (error) {
      console.error('Error fetching listings:', error);
    } finally {
      setLoading(false);
    }
  };

const handleImageUpload = async (e) => {
  const files = Array.from(e.target.files);

  const currentImageCount = formData.images.length + pendingImageFiles.length;
  const availableSlots = MAX_IMAGE_COUNT - currentImageCount;

  const filesToAdd = files.slice(0, availableSlots);
  const skippedForCount = files.length - filesToAdd.length;

  if (skippedForCount > 0) {
    toast.error(`You can upload up to ${MAX_IMAGE_COUNT} photos. ${skippedForCount} photo${skippedForCount > 1 ? 's' : ''} skipped.`);
  }

  if (filesToAdd.length === 0) {
    e.target.value = '';
    return;
  }

  e.target.value = '';
  setProcessingImages(true);
  try {
    // Compress every image client-side so uploads work reliably on mobile
    const compressed = await Promise.all(filesToAdd.map((file) => compressImage(file)));

    const nextFiles = compressed.map((file) => ({
      file,
      preview: URL.createObjectURL(file)
    }));

    setPendingImageFiles((prev) => [...prev, ...nextFiles]);
    setImagePreview((prev) => [...prev, ...nextFiles.map((item) => item.preview)]);
  } catch (error) {
    console.error('Failed to process image:', error);
    toast.error('Could not process one of the photos. Try selecting a different photo.');
  } finally {
    setProcessingImages(false);
  }
};

  const removeImage = (index) => {
    const existingImageCount = formData.images.length;

    if (index < existingImageCount) {
      setFormData((prev) => ({
        ...prev,
        images: prev.images.filter((_, i) => i !== index)
      }));
    } else {
      const pendingIndex = index - existingImageCount;

      setPendingImageFiles((prev) => {
        const target = prev[pendingIndex];
        if (target?.preview) {
          URL.revokeObjectURL(target.preview);
        }

        return prev.filter((_, i) => i !== pendingIndex);
      });
    }

    setImagePreview((prev) => prev.filter((_, i) => i !== index));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const toggleAmenity = (amenity) => {
    setFormData(prev => ({
      ...prev,
      amenities: prev.amenities.includes(amenity)
        ? prev.amenities.filter(a => a !== amenity)
        : [...prev.amenities, amenity]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setUploadingImages(true);

      // Defensive: never upload a pending file over the size limit
      const validPending = pendingImageFiles.filter((item) => item.file.size <= MAX_IMAGE_SIZE_BYTES);
      const droppedCount = pendingImageFiles.length - validPending.length;
      if (droppedCount > 0) {
        pendingImageFiles
          .filter((item) => item.file.size > MAX_IMAGE_SIZE_BYTES)
          .forEach((item) => {
            if (item.preview) URL.revokeObjectURL(item.preview);
          });
        setPendingImageFiles(validPending);
        setImagePreview([...formData.images, ...validPending.map((item) => item.preview)]);
        toast.error(`${droppedCount} photo${droppedCount > 1 ? 's' : ''} exceeded the 10MB limit and were removed. Please add smaller photos.`);
      }

      if (formData.images.length + validPending.length > MAX_IMAGE_COUNT) {
        toast.error(`You can only upload up to ${MAX_IMAGE_COUNT} photos. Remove some and try again.`);
        setUploadingImages(false);
        return;
      }

      const payload = {
        ...formData,
        newImageFiles: validPending.map((item) => item.file)
      };

      console.log('Submitting listing data:', payload);
      if (editingListing) {
        await api.updateProperty(editingListing.id, payload, setUploadProgress);
      } else {
        await api.createProperty(payload, setUploadProgress);
      }
      resetForm();
      setView('list');
      fetchUserListings();
      if (onListingChange) {
        onListingChange();
      }
    } catch (error) {
      console.error('Error saving listing:', error);
      console.error('Error details:', error.message);
      const message = (error.message || '').toLowerCase();
      if (message.includes('failed to fetch') || message.includes('network') || message.includes('load failed')) {
        toast.error('Save failed because of a network error. Check your connection, then tap "Create" to retry — your photos are still here.');
      } else if (message.includes('too large') || message.includes('size') || message.includes('limit') || message.includes('10mb')) {
        toast.error('Failed to save listing: one or more photos are too large (max 10MB each). Remove the large photos and try again.');
      } else {
        toast.error(`Failed to save listing: ${error.message}. Please check the console for details.`);
      }
    } finally {
      setUploadingImages(false);
      setUploadProgress(null);
    }
  };

  const handleEdit = (listing) => {
    setEditingListing(listing);
    setFormData({
      title: listing.title,
      description: listing.description || '',
      property_type: listing.property_type,
      price: listing.price,
      price_type: listing.price_type || 'Fixed Price',
      location: listing.location,
      city: listing.city || 'Accra',
      bedrooms: listing.bedrooms,
      bathrooms: listing.bathrooms,
      square_feet: listing.square_feet || '',
      amenities: listing.amenities || [],
      images: listing.images || []
    });
    setImagePreview(listing.images || []);
    setPendingImageFiles([]);
    setView('form');
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this listing?')) {
      try {
        await api.deleteProperty(id);
        fetchUserListings();
        if (onListingChange) {
          onListingChange();
        }
      } catch (error) {
        console.error('Error deleting listing:', error);
        toast.error('Failed to delete listing. Please try again.');
      }
    }
  };

  const resetForm = () => {
    pendingImageFiles.forEach((item) => {
      if (item.preview) {
        URL.revokeObjectURL(item.preview);
      }
    });

    setFormData({
      title: '',
      description: '',
      property_type: 'Apartment',
      price: '',
      price_type: 'Fixed Price',
      location: '',
      city: 'Accra',
      bedrooms: 1,
      bathrooms: 1,
      square_feet: '',
      amenities: [],
      images: []
    });
    setImagePreview([]);
    setPendingImageFiles([]);
    setEditingListing(null);
  };

  const handleNewListing = () => {
    resetForm();
    setView('form');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50 backdrop-blur-sm">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-8 py-6 flex items-center justify-between" style={{ backgroundImage: `url(${modalsBg})`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
          <div className="flex items-center gap-3">
            {view === 'form' && (
              <button
                onClick={() => setView('list')}
                className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition-all duration-200 mr-2"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
            )}
            <Home className="w-7 h-7 text-white" />
            <div>
              <h2 className="text-2xl font-bold text-white">
                {view === 'list' ? 'My Listings' : (editingListing ? 'Edit Listing' : 'New Listing')}
              </h2>
              <p className="text-blue-100 text-sm">
                {view === 'list' ? 'Manage your property listings' : 'Add property details and photos'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition-all duration-200"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {view === 'list' ? (
            <div className="p-8">
              {/* Add New Button */}
              <button
                onClick={handleNewListing}
                className="mb-6 px-5 py-2.5 bg-gradient-to-r from-blue-600 to-blue-500 text-white font-semibold rounded-full hover:from-blue-700 hover:to-blue-600 transition-all duration-200 shadow flex items-center justify-center gap-1.5"
              >
                <Plus className="w-4 h-4" />
                Add New Property
              </button>

              {/* Listings Grid */}
              {loading ? (
                <div className="space-y-4">
                  {[...Array(3)].map((_, index) => (
                    <SkeletonListing key={index} />
                  ))}
                </div>
              ) : listings.length === 0 ? (
                <div className="text-center py-16">
                  <Home className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                  <h3 className="text-xl font-semibold text-gray-700 mb-2">No listings yet</h3>
                  <p className="text-gray-500">Click "Add New Property" to create your first listing</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-4">
                  {listings.map((listing) => (
                    <div key={listing.id} className="bg-white border border-gray-200 rounded-2xl overflow-hidden hover:shadow-xl transition-all duration-200 flex flex-col lg:flex-row">
                      <div className="relative h-48 lg:h-auto lg:w-72 lg:flex-shrink-0">
                        <img
                          src={listing.images?.[0] || 'https://via.placeholder.com/400x300'}
                          alt={listing.title}
                          className="w-full h-full object-cover"
                        />
                        <span className={`absolute top-3 right-3 px-3 py-1 text-white text-xs font-semibold rounded-full ${
                          listing.is_available ? 'bg-green-500' : 'bg-gray-500'
                        }`}>
                          {listing.is_available ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                      <div className="p-5 flex-1 flex flex-col justify-center">
                        <h3 className="text-lg font-bold text-gray-900 mb-2">{listing.title}</h3>
                        <div className="flex items-center gap-2 text-gray-600 mb-3">
                          <MapPin className="w-4 h-4" />
                          <span className="text-sm">{listing.location}</span>
                        </div>
                        <div className="flex items-center gap-4 mb-4 text-sm text-gray-600">
                          <div className="flex items-center gap-1">
                            <Bed className="w-4 h-4" />
                            <span>{listing.bedrooms}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Bath className="w-4 h-4" />
                            <span>{listing.bathrooms}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <DollarSign className="w-4 h-4" />
                            <span className="font-semibold text-blue-600">GH₵{listing.price}/month</span>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleEdit(listing)}
                            className="flex-1 px-4 py-2 bg-blue-50 text-blue-600 font-medium rounded-lg hover:bg-blue-100 transition-colors flex items-center justify-center gap-2"
                          >
                            <Edit className="w-4 h-4" />
                            Edit
                          </button>
                          <button
                            onClick={() => handleDelete(listing.id)}
                            className="flex-1 px-4 py-2 bg-red-50 text-red-600 font-medium rounded-lg hover:bg-red-100 transition-colors flex items-center justify-center gap-2"
                          >
                            <Trash2 className="w-4 h-4" />
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="p-8 space-y-6">
              {/* Image Upload */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  Property Photos
                </label>
                <p className="text-xs text-gray-500 mb-3">
                  Upload up to 10 photos. Each photo must be less than 10MB.
                </p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                  {imagePreview.map((img, index) => (
                    <div key={index} className="relative group">
                      <img
                        src={img}
                        alt={`Preview ${index + 1}`}
                        className="w-full h-32 object-cover rounded-xl border-2 border-gray-200"
                      />
                      <button
                        type="button"
                        onClick={() => removeImage(index)}
                        className="absolute top-2 right-2 bg-red-500 text-white p-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  <label className="w-full h-32 border-2 border-dashed border-gray-300 rounded-xl flex flex-col items-center justify-center cursor-pointer hover:border-blue-400 transition-colors">
                    <ImageIcon className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                    {processingImages ? (
                      <div className="flex flex-col items-center gap-2">
                        <div className="w-5 h-5 border-2 border-blue-300 border-t-blue-600 rounded-full animate-spin"></div>
                        <p className="text-gray-500 text-sm">Processing photos...</p>
                      </div>
                    ) : (
                      <p className="text-gray-600">Tap to upload property images</p>
                    )}
                    <input
                      type="file"
                      multiple
                      accept="image/*"
                      onChange={handleImageUpload}
                      disabled={processingImages || formData.images.length + pendingImageFiles.length >= MAX_IMAGE_COUNT}
                      className="hidden"
                      id="image-upload"
                    />
                  </label>
                </div>
              </div>

              {/* Basic Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="md:col-span-2">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Property Title
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                    placeholder="e.g., Modern 2 Bedroom Apartment"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Property Type
                  </label>
                  <select
                    name="property_type"
                    value={formData.property_type}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                  >
                    {propertyTypes.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Monthly Rent (GH₵)
                  </label>
                  <input
                    type="number"
                    name="price"
                    value={formData.price}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                    placeholder="1200"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Price Type
                  </label>
                  <select
                    name="price_type"
                    value={formData.price_type}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                  >
                    <option value="Fixed Price">Fixed Price</option>
                    <option value="Negotiable">Negotiable</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Location
                  </label>
                  <input
                    type="text"
                    name="location"
                    value={formData.location}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                    placeholder="East Legon"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    City
                  </label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                    placeholder="Accra"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Bedrooms
                  </label>
                  <input
                    type="number"
                    name="bedrooms"
                    value={formData.bedrooms}
                    onChange={handleChange}
                    min="1"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Bathrooms
                  </label>
                  <input
                    type="number"
                    name="bathrooms"
                    value={formData.bathrooms}
                    onChange={handleChange}
                    min="1"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Square Feet (optional)
                  </label>
                  <input
                    type="number"
                    name="square_feet"
                    value={formData.square_feet}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                    placeholder="1200"
                  />
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows="4"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none resize-none"
                  placeholder="Describe your property..."
                />
              </div>

              {/* Amenities */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  Amenities
                </label>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                  {amenitiesList.map(amenity => (
                    <button
                      key={amenity}
                      type="button"
                      onClick={() => toggleAmenity(amenity)}
                      className={`px-4 py-2 rounded-lg font-medium transition-all ${
                        formData.amenities.includes(amenity)
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {amenity}
                    </button>
                  ))}
                </div>
              </div>

              {/* Submit */}
              {uploadingImages && (
                <div className="mt-4">
                  <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                    <span className="flex items-center gap-1.5">
                      <span className="w-3 h-3 border-2 border-blue-300 border-t-blue-600 rounded-full animate-spin inline-block"></span>
                      Uploading photos
                    </span>
                    <span className="font-semibold text-blue-600 tabular-nums">{uploadProgress ?? 0}%</span>
                  </div>
                  <div className="h-2 w-full bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-blue-600 to-blue-500 rounded-full transition-all duration-300 ease-out"
                      style={{ width: `${uploadProgress ?? 0}%` }}
                    />
                  </div>
                </div>
              )}
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    resetForm();
                    setView('list');
                  }}
                  className="flex-1 px-6 py-3 text-gray-700 font-medium rounded-xl hover:bg-gray-100 transition-all"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={uploadingImages}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-500 text-white font-semibold rounded-xl hover:from-blue-700 hover:to-blue-600 transition-all shadow-lg hover:shadow-xl flex items-center justify-center"
                >
                  {uploadingImages
                    ? `Uploading ${uploadProgress ?? 0}%`
                    : editingListing
                      ? 'Update'
                      : 'Create'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ListingsModal;
